// The extension auth surface, ported from careero/web/api/extension/extension-auth-api.ng1.ts
// and extension.user.api.ng1.ts.
//
// Endpoint paths are verbatim, including the ones whose names look like leftovers —
// `register3`, `resetPassword2` (the digits are real, and renaming them would 404), and
// `getRequiredOrigins`, which upstream has since deprecated.
//
// Every call is same-origin and cookie-authenticated (§1.4): `credentials: 'include'`, no
// Authorization header, no token. The proxy is what makes that possible from this origin.
import { request } from './request.ts'

const AUTH = '/chrome-extension-api/auth'
const USER = '/chrome-extension-api/user'

export type OAuthProvider = 'google' | 'microsoft'

/** `ExtensionUserStatus` — ordinals, and the order is the contract (0 is Pending). */
export const ExtensionUserStatus = { Pending: 0, Active: 1, Disabled: 2 } as const
export type ExtensionUserStatus = (typeof ExtensionUserStatus)[keyof typeof ExtensionUserStatus]

export interface ExtensionUser {
  id?: number
  email?: string
  name?: string
  whitelabelId?: number
  uuid?: string | null
  /** Ordinal, not a name: `1` is Active. The status screen branches on Pending. */
  status?: ExtensionUserStatus
  creationDate?: string
  updateDate?: string
  multiFactorAuthRequired?: boolean
  multiFactorAuthSetupCompleted?: boolean
  roles?: string[]
  /** False when the account has MFA enrolled but this session has not validated it (§1.3). */
  mfaValidated?: boolean
  [key: string]: unknown
}

/**
 * The call the router's `otherwise` handler depends on: it resolves to the user, or rejects
 * with a body carrying `mfaValidated: false` when MFA is pending. Both outcomes route
 * somewhere different (§1.3), so the rejection body matters as much as the success.
 */
export async function getUser(): Promise<ExtensionUser> {
  return request<ExtensionUser>('GET', USER)
}

export async function login(email: string, password: string): Promise<ExtensionUser> {
  // The MFA filter sits on POST /auth/login specifically (SecurityConfig, §1.4), so a
  // successful password check can still route to the MFA challenge rather than to status.
  return request<ExtensionUser>('POST', `${AUTH}/login`, { email, password })
}

export async function logout(): Promise<void> {
  await request<unknown>('POST', `${AUTH}/logout`)
}

/**
 * ⚠️ IT GATES THE SUBMIT BUTTON, SO IT IS BOUNDED. `extension-auth-api.ng1.ts:30-48` passes
 * a 5s timeout, added upstream after a stalled connection kept the login button disabled
 * for the browser's whole connection timeout — minutes, with nothing on screen explaining
 * it. The timer is cleared when the probe settles, which upstream does with
 * `.finally($timeout.cancel)` for the same reason.
 */
const IS_SSO_REQUIRED_TIMEOUT_MS = 5000

/**
 * GET with a query parameter — `extension-auth-api.ng1.ts:27` sends `params: { email }`, and
 * the endpoint answers a bare `true`/`false`. The first port sent a POST body and the tenant
 * replied **405**, which is the shape of every bug in this file: right path, wrong call.
 *
 * ⚠️ ONLY AN EXPLICIT `true` IS AN SSO REQUIREMENT. Upstream checks this at its one call site
 * (`sso-required-email-validator.directive.ts`); here it is checked once, where the raw
 * answer is, so no caller can forget it.
 *
 * ⚠️ AND A BODY THAT IS NOT AN ANSWER REJECTS, WITH THE RESPONSE'S METADATA (`requireJson`). A
 * **200 carrying a non-JSON body** — a proxy, a captive portal, a session wall — and a body that
 * stalls after the headers until the timer fires both reach the validator's fail-open catch as
 * a `ResponseBodyError`, which carries the status and content-type its log line reports
 * (whitelabel-ext `29621a2`, `4853064`). The timer stays armed through the body read
 * (`22f31b9`): it is cleared only once `request` — which reads the body — has settled.
 */
export async function isSsoRequired(email: string): Promise<boolean> {
  const controller = new AbortController()
  const timer = setTimeout(() => {
    controller.abort()
  }, IS_SSO_REQUIRED_TIMEOUT_MS)
  try {
    // `isSsoRequired(viewValue, true)`: the login field's validator handles a failure (fail-open),
    // so the global alert would pop up while the user is still typing.
    const answer = await request<unknown>(
      'GET',
      withQuery(`${AUTH}/isSsoRequired`, { email }),
      undefined,
      { signal: controller.signal },
      { suppressErrorDialog: true, requireJson: true },
    )
    return answer === true
  } finally {
    clearTimeout(timer)
  }
}

/** The same bound as `isSsoRequired`'s, and for the same reason (`IS_VALID_EMAIL_TIMEOUT_MS`). */
const IS_VALID_EMAIL_TIMEOUT_MS = 5000

/**
 * `extensionAuthApi.isValidEmail` (whitelabel-ext `shared/api/extension-auth.api.ts`) — GET with
 * the email in the query, answering a bare boolean. Bounded at 5s: the popup's validator fails
 * open on a rejection, but a stalled request would keep the form pending for the whole
 * connection timeout. Truthy, as upstream reads it (`if (!emailValid)`).
 */
export async function isValidEmail(email: string): Promise<boolean> {
  const controller = new AbortController()
  const timer = setTimeout(() => {
    controller.abort()
  }, IS_VALID_EMAIL_TIMEOUT_MS)
  try {
    const answer = await request<unknown>(
      'GET',
      withQuery(`${AUTH}/isValidEmail`, { email }),
      undefined,
      { signal: controller.signal },
      { suppressErrorDialog: true },
    )
    return Boolean(answer)
  } finally {
    clearTimeout(timer)
  }
}

/** POST, but the email rides in the QUERY (`params`, not `data`, upstream). */
export async function requestPasswordReset(email: string): Promise<void> {
  await request<unknown>('POST', withQuery(`${AUTH}/requestPasswordReset`, { email }))
}

/** `resetPassword2` — the digit is upstream's, not a typo. */
export async function resetPassword(token: string, password: string): Promise<void> {
  await request<unknown>('POST', `${AUTH}/resetPassword2`, { token, password })
}

export async function requestVerification(): Promise<void> {
  await request<unknown>('POST', `${AUTH}/requestVerification`)
}

/**
 * Answers `{ mfaQRBase64 }`, not a bare string — and only for a session that is enrolling.
 * The QR is absent for an account that already completed setup, which is what the validate
 * screen keys its "Can't find it?" link off.
 */
export async function getMultiFactorQrImage(): Promise<string | undefined> {
  const response = await request<{ mfaQRBase64?: string }>('GET', `${AUTH}/getMultiFactorQRBase64Image`)
  return response.mfaQRBase64
}

/** The field is `validationCode` and it is a NUMBER; `{ code }` is silently ignored. */
export async function validateMultiFactorAuth(validationCode: number): Promise<void> {
  // `validateMultiFactorAuth(code, true)`: a wrong code is the screen's own inline error.
  await request<unknown>('POST', `${AUTH}/validateMultiFactorAuth`, { validationCode }, {}, { suppressErrorDialog: true })
}

/** Takes the email: this runs when the user CANNOT complete MFA, so there is no session. */
export async function requestMultiFactorAuthReset(email: string): Promise<void> {
  await request<unknown>('POST', `${AUTH}/requestMultiFactorAuthReset`, { email })
}

/**
 * `register3` — the digit is upstream's. `gRecaptchaResponse` is required by the signature
 * even where the tenant sets `captchaValidationDisabled`, so it is passed through rather
 * than omitted: a tenant with validation ON would reject a request that never carried it.
 */
export async function register(
  name: string,
  email: string,
  password: string,
  gRecaptchaResponse: string,
): Promise<ExtensionUser> {
  return request<ExtensionUser>('POST', `${AUTH}/register3`, {
    name,
    email,
    password,
    gRecaptchaResponse,
    role: 'extension-user',
  })
}

/**
 * `register2` — the same registration without a captcha, and the extension's own endpoint.
 *
 * ⚠️ NOT AN ALTERNATIVE TO `register`: IT IS THE ONE THE POPUP CAN REACH. careero's SPA
 * mounts a reCAPTCHA widget, which means loading `https://www.google.com/recaptcha/…` — and
 * an MV3 extension page's CSP is `script-src 'self'` (manifest.config.ts), so the script
 * cannot be loaded there at all. `whitelabel-ext`'s popup calls `register2` for exactly this
 * reason (`shared/api/extension-auth.api.ts:167`), and `SecurityConfig` applies the captcha
 * filter to `register3` alone.
 *
 * It is `@Deprecated` upstream and `@PermitAll`; the deprecation is not a plan to remove it
 * while the extension is the only client that can use it. The SPA must keep calling
 * `register` — switching it here would quietly take the captcha off careero's public sign-up.
 */
export async function registerLegacy(name: string, email: string, password: string): Promise<ExtensionUser> {
  return request<ExtensionUser>('POST', `${AUTH}/register2`, {
    name,
    email,
    password,
    role: 'extension-user',
  })
}

/** `POST /chrome-extension-api/user` — the whole DTO goes back, not a patch. */
export async function updateUser(user: ExtensionUser): Promise<ExtensionUser> {
  return request<ExtensionUser>('POST', USER, user)
}

function withQuery(path: string, params: Record<string, string>): string {
  return `${path}?${new URLSearchParams(params).toString()}`
}

/**
 * Upstream builds this against `window.location.origin`, which is correct here for the same
 * reason the proxy exists: the OAuth round-trip must start and end on one origin, or the
 * session cookie it sets is dropped.
 *
 * `?redirect_uri=` repeats the same URL and is load-bearing, not decoration —
 * `extension-auth-api.ng1.ts:179` sends it so Spring returns to the origin that started the
 * flow rather than to its configured default. The first port of this dropped it, which on
 * this origin means the round-trip ends on woo.io and the SPA never sees the session.
 *
 * Distinct from the Gmail / Outlook connect (`./email-integration.ts`), which is a different
 * flow with a different mechanism: Track B's `ExtensionOAuthRedirectResolver` governs that
 * one's return origin, not this one.
 */
export function oAuthSignInUrl(provider: OAuthProvider, origin: string = window.location.origin): string {
  const signInUrl = `${origin}${AUTH}/oauth2/authorization/${provider}`
  return `${signInUrl}?redirect_uri=${signInUrl}`
}
